import React, { useEffect, useMemo, useRef, useState } from "react";
import mqtt from "mqtt";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";

import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import Battery6BarRoundedIcon from "@mui/icons-material/Battery6BarRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import SouthRoundedIcon from "@mui/icons-material/SouthRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import DeviceHubRoundedIcon from "@mui/icons-material/DeviceHubRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

const BROKER_URL = "wss://test.mosquitto.org:8081/mqtt";

/* -------------------- TOPICS -------------------- */
const TOPIC_IN = "fludee/device/FLD-10";
const TOPIC_OUT = "fludee/out/live";

/* -------------------- COLORS -------------------- */
const Colors = {
  bg: {
    app: "#eef4fb",
    paper: "#ffffff",
    dark: "#0f172a",
    panelSoft: "#f8fbff",
  },
  brand: {
    primary: "#1d4ed8",
    secondary: "#0ea5e9",
  },
  text: {
    title: "#0f172a",
    body: "#1e293b",
    muted: "#64748b",
    sub: "#94a3b8",
    icon: "#475569",
    white: "#ffffff",
  },
  border: {
    light: "#dbe7f3",
    topLine: "#edf2f7",
  },
  status: {
    success: "#16a34a",
    warning: "#f59e0b",
    danger: "#dc2626",
    info: "#2563eb",
    idle: "#64748b",
  },
  shadow: {
    brand: "0 12px 35px rgba(15, 23, 42, 0.08)",
  },
};

const dripFall = keyframes`
  0% { transform: translate(-50%, 0px) scale(0.75); opacity: 0.15; }
  20% { opacity: 1; }
  100% { transform: translate(-50%, 88px) scale(1.02); opacity: 0; }
`;

const urineWave = keyframes`
  0% { transform: scaleY(0.35); opacity: 0.45; }
  50% { transform: scaleY(1); opacity: 1; }
  100% { transform: scaleY(0.35); opacity: 0.45; }
`;

const alarmFlash = keyframes`
  0% { box-shadow: 0 0 0 ${alpha(Colors.status.danger, 0)}; }
  50% { box-shadow: 0 0 18px ${alpha(Colors.status.danger, 0.28)}; }
  100% { box-shadow: 0 0 0 ${alpha(Colors.status.danger, 0)}; }
`;

const flowDash = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 24px 0; }
`;

/* -------------------- HELPERS -------------------- */
function fmt(v, suffix = "") {
  if (v === undefined || v === null || v === "") return "—";
  return `${v}${suffix}`;
}

function cleanLabel(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value).replace(/^[A-Za-z/ ]+\s*-\s*/i, "").trim();
}

function toNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function upperText(value = "") {
  return String(value || "").trim().toUpperCase();
}

function getStatusTheme(status) {
  const normalized = upperText(status);

  switch (normalized) {
    case "READY":
      return {
        bg: alpha(Colors.status.success, 0.12),
        color: Colors.status.success,
        border: alpha(Colors.status.success, 0.25),
      };
    case "RUNNING":
    case "ACTIVE":
      return {
        bg: alpha(Colors.status.info, 0.12),
        color: Colors.status.info,
        border: alpha(Colors.status.info, 0.25),
      };
    case "STOPPED":
    case "PAUSED":
      return {
        bg: alpha(Colors.status.warning, 0.14),
        color: Colors.status.warning,
        border: alpha(Colors.status.warning, 0.25),
      };
    case "ALERT":
    case "NO FLOW":
    case "ERROR":
      return {
        bg: alpha(Colors.status.danger, 0.12),
        color: Colors.status.danger,
        border: alpha(Colors.status.danger, 0.25),
      };
    default:
      return {
        bg: alpha(Colors.status.idle, 0.12),
        color: Colors.status.idle,
        border: alpha(Colors.status.idle, 0.22),
      };
  }
}

/* -------------------- UI SMALL COMPONENTS -------------------- */
function Panel({ title, right, children, sx = {} }) {
  return (
    <Card
      sx={{
        borderRadius: 4,
        background: Colors.bg.paper,
        border: `1px solid ${Colors.border.light}`,
        boxShadow: Colors.shadow.brand,
        height: "100%",
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: { xs: 16, md: 18 },
              fontWeight: 900,
              color: Colors.text.title,
            }}
          >
            {title}
          </Typography>
          {right}
        </Stack>
        <Divider sx={{ borderColor: Colors.border.light, mb: 1.25 }} />
        {children}
      </CardContent>
    </Card>
  );
}

function DetailLine({ label, value }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={1}
      sx={{
        py: 0.8,
        borderBottom: `1px solid ${Colors.border.topLine}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: Colors.text.muted,
          minWidth: 110,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: Colors.text.body,
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function InfoLabel({ label, value, big = false }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: Colors.text.muted, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.3,
          fontSize: big ? { xs: 18, md: 24 } : { xs: 14, md: 16 },
          fontWeight: 900,
          color: Colors.text.title,
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/* -------------------- ANIMATIONS -------------------- */
function AnimatedDripBottle({ speed = 0, active = false }) {
  const duration = Math.max(0.6, 2.6 - Number(speed || 0) / 65);

  return (
    <Box sx={{ position: "relative", width: 96, height: 138, mx: "auto", opacity: active ? 1 : 0.55 }}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 16,
          height: 16,
          borderRadius: "6px 6px 0 0",
          border: `2px solid ${Colors.brand.primary}`,
          borderBottom: "none",
          bgcolor: Colors.bg.paper,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 12,
          left: "50%",
          transform: "translateX(-50%)",
          width: 62,
          height: 68,
          borderRadius: "14px 14px 16px 16px",
          border: `2px solid ${Colors.brand.primary}`,
          bgcolor: alpha(Colors.brand.primary, 0.05),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "58%",
            bgcolor: alpha(Colors.status.success, 0.22),
            borderTop: `2px solid ${Colors.status.success}`,
          }}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 82,
          left: "50%",
          transform: "translateX(-50%)",
          width: 20,
          height: 24,
          borderRadius: "9px 9px 11px 11px",
          border: `2px solid ${Colors.brand.primary}`,
          bgcolor: alpha(Colors.brand.primary, 0.04),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 104,
          left: "50%",
          transform: "translateX(-50%)",
          width: 3,
          height: 26,
          bgcolor: alpha(Colors.brand.primary, active ? 0.75 : 0.25),
          borderRadius: 999,
        }}
      />
      {[0, 0.5, 1].map((delay, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: 95,
            left: "50%",
            width: 8,
            height: 12,
            borderRadius: "50% 50% 55% 55%",
            bgcolor: Colors.brand.primary,
            animation: active ? `${dripFall} ${duration}s linear ${delay}s infinite` : "none",
            opacity: active ? 1 : 0,
          }}
        />
      ))}
      <Typography
        sx={{
          position: "absolute",
          bottom: -4,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 11,
          fontWeight: 800,
          color: active ? Colors.status.success : Colors.text.muted,
        }}
      >
        {active ? "Drip Running" : "Drip Stopped"}
      </Typography>
    </Box>
  );
}

function AnimatedUrineBag({ value = 0, active = false }) {
  const fillPercent = Math.max(12, Math.min(85, Math.round((Number(value) || 0) / 6)));
  const urineYellow = "#D9B63D";

  return (
    <Box sx={{ position: "relative", width: 110, height: 150, mx: "auto", opacity: active ? 1 : 0.7 }}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 22,
          height: 18,
          border: `2px solid ${Colors.brand.primary}`,
          borderBottom: "none",
          borderRadius: "10px 10px 0 0",
          bgcolor: Colors.bg.paper,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          width: 78,
          height: 110,
          border: `2px solid ${Colors.brand.primary}`,
          borderRadius: "16px 16px 18px 18px",
          overflow: "hidden",
          bgcolor: alpha(Colors.brand.primary, 0.04),
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: `${fillPercent}%`,
            bgcolor: alpha(urineYellow, 0.46),
            borderTop: `2px solid ${urineYellow}`,
            animation: active ? `${urineWave} 1.6s ease-in-out infinite` : "none",
            transformOrigin: "bottom",
          }}
        />
        <Stack justifyContent="center" alignItems="center" sx={{ position: "absolute", inset: 0 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 900, color: Colors.text.title }}>
            {fmt(value)}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: Colors.text.body }}>
            ml
          </Typography>
        </Stack>
      </Box>
      <Typography
        sx={{
          position: "absolute",
          bottom: -4,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 11,
          fontWeight: 800,
          color: active ? urineYellow : Colors.text.muted,
        }}
      >
        {active ? "Output Updating" : "Output Stable"}
      </Typography>
    </Box>
  );
}

/* -------------------- ALERT PANEL -------------------- */
function AlarmPanel({ status, error }) {
  const danger =
    upperText(status) === "ALERT" ||
    upperText(status) === "ERROR" ||
    upperText(status) === "NO FLOW" ||
    Boolean(error);

  return (
    <Card
      sx={{
        borderRadius: 4,
        border: `1px solid ${Colors.border.light}`,
        background: danger ? alpha(Colors.status.danger, 0.06) : Colors.bg.paper,
        boxShadow: Colors.shadow.brand,
        height: "100%",
        animation: danger ? `${alarmFlash} 1.3s infinite` : "none",
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack spacing={1}>
          <Typography sx={{ fontSize: 16, fontWeight: 900, color: Colors.text.title }}>
            Alarm Panel
          </Typography>
          <Divider sx={{ borderColor: Colors.border.light }} />
          <Chip
            icon={
              danger ? (
                <WarningAmberRoundedIcon sx={{ color: `${Colors.text.white} !important` }} />
              ) : undefined
            }
            label={danger ? (error || status || "Alert") : "Normal"}
            sx={{
              bgcolor: danger ? Colors.status.danger : Colors.status.success,
              color: Colors.text.white,
              fontWeight: 900,
              width: "fit-content",
            }}
          />
          <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
            {danger
              ? "Attention required. Check device status, payload mapping, or MQTT connectivity."
              : "No active alarm. Device data is healthy."}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

/* -------------------- BALANCE CHART -------------------- */
function HumanFluidBalanceChart({ fluidIn = 0, fluidOut = 0, netBalance = 0 }) {
  const safeIn = Number(fluidIn) || 0;
  const safeOut = Number(fluidOut) || 0;
  const safeNet = Number(netBalance) || 0;
  const positive = safeNet >= 0;
  const urineYellow = "#D9B63D";

  const intakeWidth = Math.max(12, Math.min(100, Math.round(safeIn / 10)));
  const outputWidth = Math.max(12, Math.min(100, Math.round(safeOut / 10)));

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: { xs: 290, md: 320 },
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        bgcolor: "#f8fbff",
        overflow: "hidden",
        px: 1.2,
        py: 1.5,
      }}
    >
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} md={3}>
          <Stack spacing={1} alignItems={{ xs: "center", md: "flex-start" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: Colors.text.title }}>
              Fluid In
            </Typography>
            <Box
              sx={{
                width: "100%",
                maxWidth: 160,
                p: 1,
                borderRadius: 2,
                border: `1px solid ${alpha(Colors.status.success, 0.25)}`,
                bgcolor: alpha(Colors.status.success, 0.1),
              }}
            >
              <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700 }}>
                Input
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: Colors.text.body }}>
                {safeIn} ml
              </Typography>
            </Box>
            <Box
              sx={{
                width: "100%",
                maxWidth: 160,
                height: 9,
                borderRadius: 999,
                bgcolor: alpha(Colors.status.success, 0.12),
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${intakeWidth}%`,
                  height: "100%",
                  bgcolor: Colors.status.success,
                  borderRadius: 999,
                  transition: "width 0.5s ease",
                }}
              />
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: { xs: 220, md: 240 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: { xs: "4%", md: "2%" },
                top: "40%",
                width: { xs: "30%", md: "32%" },
                height: 4,
                backgroundImage: `linear-gradient(90deg, ${Colors.status.success} 50%, transparent 50%)`,
                backgroundSize: "14px 4px",
                backgroundRepeat: "repeat-x",
                animation: `${flowDash} 1.1s linear infinite`,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                right: { xs: "4%", md: "2%" },
                top: "58%",
                width: { xs: "30%", md: "32%" },
                height: 4,
                backgroundImage: `linear-gradient(90deg, ${urineYellow} 50%, transparent 50%)`,
                backgroundSize: "14px 4px",
                backgroundRepeat: "repeat-x",
                animation: `${flowDash} 1.1s linear infinite`,
              }}
            />
            <Box
              sx={{
                width: { xs: 120, md: 140 },
                height: { xs: 180, md: 200 },
                borderRadius: "70px 70px 60px 60px",
                bgcolor: alpha(Colors.brand.primary, 0.08),
                border: `2px solid ${alpha(Colors.brand.primary, 0.25)}`,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 28,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 34,
                  height: 72,
                  borderRadius: "40px",
                  bgcolor: alpha(Colors.status.success, 0.18),
                  border: `2px solid ${alpha(Colors.status.success, 0.55)}`,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 34,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: alpha(urineYellow, 0.22),
                  border: `2px solid ${alpha(urineYellow, 0.55)}`,
                }}
              />
            </Box>

            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                px: 1.6,
                py: 0.55,
                borderRadius: 999,
                bgcolor: positive
                  ? alpha(Colors.status.success, 0.14)
                  : alpha(Colors.status.danger, 0.14),
                border: `1px solid ${
                  positive
                    ? alpha(Colors.status.success, 0.28)
                    : alpha(Colors.status.danger, 0.28)
                }`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: positive ? Colors.status.success : Colors.status.danger,
                }}
              >
                Net {positive ? "+" : ""}
                {safeNet} ml
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Stack spacing={1} alignItems={{ xs: "center", md: "flex-end" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: Colors.text.title }}>
              Fluid Out
            </Typography>
            <Box
              sx={{
                width: "100%",
                maxWidth: 160,
                p: 1,
                borderRadius: 2,
                border: `1px solid ${alpha(urineYellow, 0.28)}`,
                bgcolor: alpha(urineYellow, 0.15),
              }}
            >
              <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700 }}>
                Output
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: Colors.text.body }}>
                {safeOut} ml
              </Typography>
            </Box>
            <Box
              sx={{
                width: "100%",
                maxWidth: 160,
                height: 9,
                borderRadius: 999,
                bgcolor: alpha(urineYellow, 0.15),
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${outputWidth}%`,
                  height: "100%",
                  bgcolor: urineYellow,
                  borderRadius: 999,
                  transition: "width 0.5s ease",
                }}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

/* -------------------- NORMALIZERS -------------------- */
function normalizeIncomingMessage(json) {
  const payload = json?.payload || {};

  const bagVolumeMl = toNumber(payload?.bag_volume_ml, 0);
  const infusedMl = toNumber(payload?.infused_ml, 0);
  const remainingMl = Math.max(0, bagVolumeMl - infusedMl);

  const displayRateValue = toNumber(payload?.display_rate_value, 0);
  const avgSpeedMlHr = toNumber(payload?.avg_speed_ml_hr, 0);

  return {
    raw: json,
    device_id: json?.device_id || "",
    topic: json?.topic || "",
    flow_in: toNumber(json?.flow_in, 0),
    flow_out: toNumber(json?.flow_out, 0),
    battery: clamp(toNumber(json?.battery, 0), 0, 100),
    status: json?.status || "NO DATA",
    payload: {
      patient_name: payload?.patient_name || "",
      doctor_name: payload?.doctor_name || "",
      uhid: payload?.uhid || "",
      ward_bed: payload?.ward_bed || "",
      device_local_id: payload?.device_local_id || "",
      hospital: payload?.hospital || "",
      fluid_type: payload?.fluid_type || "",
      bag_volume_ml: bagVolumeMl,
      drip_factor: toNumber(payload?.drip_factor, 0),
      display_rate_value: displayRateValue,
      display_rate_unit: payload?.display_rate_unit || "drop/min",
      total_drops: toNumber(payload?.total_drops, 0),
      infused_ml: infusedMl,
      remaining_ml: remainingMl,
      runtime: payload?.runtime || "00:00:00",
      avg_speed_ml_hr: avgSpeedMlHr,
      session_id: payload?.session_id ?? 0,
      infusion_count: payload?.infusion_count ?? 0,
      rtc_date: payload?.rtc_date || "",
      drip_rate_dpm:
        payload?.display_rate_unit === "drop/min" ? displayRateValue : 0,
      total_time: payload?.runtime || "00:00:00",
      etc_value:
        remainingMl > 0 && avgSpeedMlHr > 0
          ? `${Math.floor(remainingMl / avgSpeedMlHr)
              .toString()
              .padStart(2, "0")}:${Math.floor(((remainingMl / avgSpeedMlHr) * 60) % 60)
              .toString()
              .padStart(2, "0")}:00`
          : "—",
    },
  };
}

function normalizeOutMessage(json) {
  const payload = json?.payload || {};

  const currentVolumeMl = toNumber(payload?.current_volume_ml, 0);
  const bagCapacityMl = toNumber(payload?.bag_capacity_ml, 0);
  const outPercent =
    bagCapacityMl > 0 ? clamp((currentVolumeMl / bagCapacityMl) * 100, 0, 100) : 0;

  const derivedStatus = payload?.bag_full_alert
    ? "ALERT"
    : payload?.session_paused
    ? "PAUSED"
    : payload?.session_active
    ? "RUNNING"
    : json?.device_status || "READY";

  return {
    raw: json,
    device_id: json?.device_id || "",
    topic: json?.topic || "",
    status: upperText(derivedStatus),
    device_status: json?.device_status || "Ready",
    wifi_connected: Boolean(json?.wifi_connected),
    mqtt_connected: Boolean(json?.mqtt_connected),
    payload: {
      patient_id: payload?.patient_id ?? 0,
      fluid_type: payload?.fluid_type || "Not Set",
      fluid_density_g_per_ml: toNumber(payload?.fluid_density_g_per_ml, 1),
      bag_capacity_ml: bagCapacityMl,
      bag_weight_gm: toNumber(payload?.bag_weight_gm, 0),
      raw_weight_g: toNumber(payload?.raw_weight_g, 0),
      net_weight_g: toNumber(payload?.net_weight_g, 0),
      current_volume_ml: currentVolumeMl,
      output_speed_ml_hr: toNumber(payload?.output_speed_ml_hr, 0),
      session_active: Boolean(payload?.session_active),
      session_paused: Boolean(payload?.session_paused),
      bag_full_alert: Boolean(payload?.bag_full_alert),
      date: payload?.date || "--/--/--",
      time: payload?.time || "--:--:-- --",
      fill_percent: outPercent,
    },
  };
}

/* -------------------- MAIN DASHBOARD -------------------- */
export default function Dashboard() {
  const [connected, setConnected] = useState(false);

  const [deviceData, setDeviceData] = useState(null); // IN
  const [outDeviceData, setOutDeviceData] = useState(null); // OUT

  const [lastRawPayload, setLastRawPayload] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const clientRef = useRef(null);

  useEffect(() => {
    const client = mqtt.connect(BROKER_URL, {
      clean: true,
      reconnectPeriod: 3000,
      connectTimeout: 10000,
      clientId: `fludee-web-${Math.random().toString(16).slice(2, 10)}`,
    });

    clientRef.current = client;

    client.on("connect", () => {
      setConnected(true);
      setError("");

      client.subscribe(TOPIC_IN, { qos: 0 }, (err) => {
        if (err) setError("Failed to subscribe FLUDEE IN topic");
      });

      client.subscribe(TOPIC_OUT, { qos: 0 }, (err) => {
        if (err) setError("Failed to subscribe FLUDEE OUT topic");
      });
    });

    client.on("reconnect", () => {
      setConnected(false);
    });

    client.on("close", () => {
      setConnected(false);
    });

    client.on("offline", () => {
      setConnected(false);
    });

    client.on("error", (err) => {
      setError(err?.message || "MQTT connection error");
    });

    client.on("message", (topic, message) => {
      const raw = message.toString();

      try {
        const json = JSON.parse(raw);

        if (topic === TOPIC_IN) {
          setDeviceData(normalizeIncomingMessage(json));
        }

        if (topic === TOPIC_OUT) {
          setOutDeviceData(normalizeOutMessage(json));
        }

        setLastRawPayload(raw);
        setLastUpdated(new Date().toLocaleTimeString());
        setError("");
        setLoading(false);
      } catch (err) {
        console.error("MQTT JSON parse error:", err);
        setError("Invalid JSON received from MQTT");
        setLoading(false);
      }
    });

    const loadingTimer = setTimeout(() => {
      setLoading(false);
    }, 4000);

    return () => {
      clearTimeout(loadingTimer);
      if (clientRef.current) {
        clientRef.current.end(true);
      }
    };
  }, []);

  /* ---------- IN DATA ---------- */
  const payload = deviceData?.payload || {};
  const battery = toNumber(deviceData?.battery, 0);
  const flowIn = toNumber(deviceData?.flow_in, 0);
  const infusedMl = toNumber(payload?.infused_ml, 0);
  const remainingMl = toNumber(payload?.remaining_ml, 0);
  const bagVolume = toNumber(payload?.bag_volume_ml, 0);
  const displayRate = toNumber(payload?.display_rate_value, 0);
  const batteryPercent = clamp(battery, 0, 100);

  const infusionPercent =
    bagVolume > 0 ? clamp((infusedMl / bagVolume) * 100, 0, 100) : 0;

  const statusText = deviceData?.status || "NO DATA";
  const statusTheme = getStatusTheme(statusText);

  const dripRunning =
    connected &&
    (upperText(statusText) === "RUNNING" || upperText(statusText) === "READY") &&
    displayRate > 0;

  /* ---------- OUT DATA ---------- */
  const outPayload = outDeviceData?.payload || {};
  const outCurrentVolumeMl = toNumber(outPayload?.current_volume_ml, 0);
  const outSpeedMlHr = toNumber(outPayload?.output_speed_ml_hr, 0);
  const outBagCapacityMl = toNumber(outPayload?.bag_capacity_ml, 0);
  const outFillPercent = toNumber(outPayload?.fill_percent, 0);
  const outStatusText = outDeviceData?.status || "NO DATA";
  const outStatusTheme = getStatusTheme(outStatusText);

  const flowOut = outCurrentVolumeMl;

  const outputRunning =
    connected &&
    Boolean(outPayload?.session_active) &&
    !Boolean(outPayload?.session_paused);

  /* ---------- COMBINED ---------- */
  const netBalance = infusedMl - flowOut;

  const sessionProgress = useMemo(() => {
    if (!bagVolume) return 0;
    return clamp((infusedMl / bagVolume) * 100, 0, 100);
  }, [infusedMl, bagVolume]);

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 1.2, md: 1.8 }, background: Colors.bg.app }}>
      <Stack spacing={1.4}>
        {/* HEADER */}
        <Card
          sx={{
            borderRadius: 4,
            background: Colors.bg.paper,
            border: `1px solid ${Colors.border.light}`,
            boxShadow: Colors.shadow.brand,
          }}
        >
          <CardContent sx={{ p: { xs: 1.6, md: 2.2 } }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", lg: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 22, md: 30 },
                    fontWeight: 900,
                    color: Colors.text.title,
                    lineHeight: 1.1,
                  }}
                >
                  FLUDEE SMART INFUSION + OUTPUT MONITORING
                </Typography>
                <Typography sx={{ fontSize: 13, color: Colors.text.muted, mt: 0.5 }}>
                  FLUDEE IN and FLUDEE OUT live cloud payload dashboard
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1} alignItems="stretch" flexWrap="wrap">
                <Chip
                  icon={<WifiRoundedIcon sx={{ color: `${Colors.text.white} !important` }} />}
                  label={connected ? " MQTT Connected" : " MQTT Disconnected"}
                  sx={{
                    bgcolor: connected ? Colors.status.success : Colors.status.danger,
                    color: Colors.text.white,
                    fontWeight: 900,
                    height: 40,
                  }}
                />
                <Chip
                  icon={<DeviceHubRoundedIcon sx={{ color: `${Colors.text.white} !important` }} />}
                  label={deviceData?.device_id ? `IN ${deviceData.device_id}` : "IN Waiting"}
                  sx={{
                    bgcolor: Colors.brand.primary,
                    color: Colors.text.white,
                    fontWeight: 900,
                    height: 40,
                  }}
                />
                <Chip
                  icon={<DeviceHubRoundedIcon sx={{ color: `${Colors.text.white} !important` }} />}
                  label={outDeviceData?.device_id ? `OUT ${outDeviceData.device_id}` : "OUT Waiting"}
                  sx={{
                    bgcolor: "#b8860b",
                    color: Colors.text.white,
                    fontWeight: 900,
                    height: 40,
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* LOADING */}
        {loading && !deviceData && !outDeviceData ? (
          <Card
            sx={{
              borderRadius: 4,
              background: Colors.bg.paper,
              border: `1px solid ${Colors.border.light}`,
              boxShadow: Colors.shadow.brand,
            }}
          >
            <CardContent sx={{ py: 8, textAlign: "center" }}>
              <CircularProgress sx={{ color: Colors.brand.primary }} />
              <Typography sx={{ mt: 2, color: Colors.text.title, fontWeight: 800 }}>
                Waiting for FLUDEE IN / OUT live MQTT data...
              </Typography>
            </CardContent>
          </Card>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}

        {/* PATIENT TOP BAR */}
        <Card
          sx={{
            borderRadius: 4,
            background: Colors.bg.paper,
            border: `1px solid ${Colors.border.light}`,
            boxShadow: Colors.shadow.brand,
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 900, color: Colors.text.title }}>
                  Patient: {cleanLabel(payload?.patient_name)}
                </Typography>
                <Typography sx={{ color: Colors.text.muted, fontSize: 14 }}>
                  | UHID: {cleanLabel(payload?.uhid)} | Bed: {cleanLabel(payload?.ward_bed)}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Battery6BarRoundedIcon sx={{ fontSize: 16, color: Colors.text.icon }} />
                  <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
                    IN Battery: {fmt(deviceData?.battery, "%")}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <AccessTimeRoundedIcon sx={{ fontSize: 16, color: Colors.text.icon }} />
                  <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
                    IN RTC: {fmt(payload?.rtc_date)}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <NotificationsNoneRoundedIcon sx={{ fontSize: 18, color: Colors.text.icon }} />
                  <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
                    Last UI Update: {lastUpdated || "Waiting"}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={1.4}>
          {/* PATIENT DETAILS */}
          <Grid item xs={12} xl={3.1}>
            <Panel title="Patient Details" right={<PersonRoundedIcon sx={{ color: Colors.text.icon }} />}>
              <Stack spacing={0.1}>
                <DetailLine label="Patient" value={cleanLabel(payload?.patient_name)} />
                <DetailLine label="Doctor" value={cleanLabel(payload?.doctor_name)} />
                <DetailLine label="UHID" value={cleanLabel(payload?.uhid)} />
                <DetailLine label="Ward / Bed" value={cleanLabel(payload?.ward_bed)} />
                <DetailLine label="Hospital" value={cleanLabel(payload?.hospital)} />
                <DetailLine label="Device Local" value={cleanLabel(payload?.device_local_id)} />
                <DetailLine label="OUT Patient ID" value={fmt(outPayload?.patient_id)} />
              </Stack>
            </Panel>
          </Grid>

          {/* FLUID IN */}
          <Grid item xs={12} md={6} xl={2.5}>
            <Panel
              title="Fluid In"
              right={<MemoryRoundedIcon sx={{ color: Colors.text.icon }} />}
              sx={{ background: alpha(Colors.status.success, 0.05) }}
            >
              <Stack spacing={1} alignItems="center" sx={{ textAlign: "center" }}>
                <AnimatedDripBottle speed={displayRate} active={dripRunning} />

                <Stack direction="row" spacing={1} alignItems="center">
                  <WaterDropRoundedIcon sx={{ color: Colors.brand.primary }} />
                  <Typography sx={{ fontSize: 15, fontWeight: 900, color: Colors.text.title }}>
                    CURRENT RATE
                  </Typography>
                </Stack>

                <Typography sx={{ fontSize: { xs: 34, md: 46 }, fontWeight: 900, lineHeight: 1, color: Colors.text.title }}>
                  {fmt(payload?.display_rate_value)}
                </Typography>

                <Typography sx={{ fontSize: 15, fontWeight: 800, color: Colors.text.body }}>
                  {payload?.display_rate_unit || "drop/min"}
                </Typography>

                <Divider sx={{ width: "100%", borderColor: Colors.border.light }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <InfoLabel label="Average Speed" value={fmt(payload?.avg_speed_ml_hr, " ml/hr")} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel label="Bag Volume" value={fmt(payload?.bag_volume_ml, " ml")} />
                  </Grid>
                </Grid>
              </Stack>
            </Panel>
          </Grid>

          {/* BALANCE */}
          <Grid item xs={12} xl={4}>
            <Panel title="Fluid Balance Chart" right={<LocalHospitalRoundedIcon sx={{ color: Colors.text.icon }} />}>
              <HumanFluidBalanceChart
                fluidIn={infusedMl}
                fluidOut={flowOut}
                netBalance={netBalance}
              />
            </Panel>
          </Grid>

          {/* FLUID OUT */}
          <Grid item xs={12} md={6} xl={2.4}>
            <Panel
              title="Fluid Out"
              right={<SouthRoundedIcon sx={{ color: Colors.text.icon }} />}
              sx={{ background: alpha("#D9B63D", 0.12) }}
            >
              <Stack spacing={1} alignItems="center" sx={{ textAlign: "center" }}>
                <AnimatedUrineBag value={outCurrentVolumeMl} active={outputRunning} />

                <Typography sx={{ fontSize: 15, fontWeight: 900, color: Colors.text.title }}>
                  CURRENT OUTPUT
                </Typography>

                <Typography sx={{ fontSize: { xs: 34, md: 46 }, fontWeight: 900, lineHeight: 1, color: Colors.text.title }}>
                  {fmt(outCurrentVolumeMl)}
                </Typography>

                <Typography sx={{ fontSize: 15, fontWeight: 800, color: Colors.text.body }}>
                  ml
                </Typography>

                <Divider sx={{ width: "100%", borderColor: Colors.border.light }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <InfoLabel label="Speed" value={fmt(outSpeedMlHr, " ml/hr")} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel label="Out Type" value={fmt(outPayload?.fluid_type)} />
                  </Grid>
                </Grid>
              </Stack>
            </Panel>
          </Grid>
          
          {/* ALARM */}
          <Grid item xs={12} lg={4.5}>
            <AlarmPanel
              status={upperText(outStatusText) === "ALERT" ? outStatusText : statusText}
              error={error}
            />
          </Grid>

          {/* FLUDEE IN SESSION */}
          <Grid item xs={12} lg={7.5}>
            <Panel title="FLUDEE IN Live Session Overview" right={<TrendingUpRoundedIcon sx={{ color: Colors.text.icon }} />}>
              <Stack spacing={1.5}>
                <Grid container spacing={1.2}>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Status" value={statusText} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Battery" value={fmt(deviceData?.battery, "%")} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Infused" value={fmt(payload?.infused_ml, " ml")} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Remaining" value={fmt(payload?.remaining_ml, " ml")} />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    px: 1.2,
                    py: 1,
                    borderRadius: 3,
                    background: statusTheme.bg,
                    border: `1px solid ${statusTheme.border}`,
                    width: "fit-content",
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 900, color: statusTheme.color }}>
                    IN Device Status: {statusText}
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700 }}>
                  Infusion Progress
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={infusionPercent}
                  sx={{
                    height: 12,
                    borderRadius: 999,
                    bgcolor: alpha(Colors.brand.primary, 0.12),
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      background: `linear-gradient(90deg, ${Colors.brand.primary}, ${Colors.status.success})`,
                    },
                  }}
                />

                <Grid container spacing={1.2}>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Runtime" value={fmt(payload?.runtime)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Avg Speed" value={fmt(payload?.avg_speed_ml_hr, " ml/hr")} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Total Drops" value={fmt(payload?.total_drops)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Session ID" value={fmt(payload?.session_id)} />
                  </Grid>
                </Grid>
              </Stack>
            </Panel>
          </Grid>

          {/* ALARM */}
          <Grid item xs={12} lg={4.5}>
            <AlarmPanel
              status={upperText(outStatusText) === "ALERT" ? outStatusText : statusText}
              error={error}
            />
          </Grid>

          {/* OUT DEVICE STATUS */}
          <Grid item xs={12} md={6}>
            <Panel
              title="FLUDEE OUT Device Status"
              right={<DeviceHubRoundedIcon sx={{ color: Colors.text.icon }} />}
              sx={{ background: alpha("#D9B63D", 0.07) }}
            >
              <Stack spacing={1.2}>
                <Grid container spacing={1.2}>
                  <Grid item xs={6}>
                    <InfoLabel label="Device ID" value={fmt(outDeviceData?.device_id)} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel label="Topic" value={fmt(outDeviceData?.topic)} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel label="Device Status" value={fmt(outDeviceData?.device_status)} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLabel label="Mapped Status" value={fmt(outStatusText)} />
                  </Grid>
                </Grid>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={outDeviceData?.wifi_connected ? "WiFi Connected" : "WiFi Disconnected"}
                    sx={{
                      bgcolor: outDeviceData?.wifi_connected
                        ? Colors.status.success
                        : Colors.status.danger,
                      color: Colors.text.white,
                      fontWeight: 900,
                    }}
                  />
                  <Chip
                    label={outDeviceData?.mqtt_connected ? "MQTT Connected" : "MQTT Disconnected"}
                    sx={{
                      bgcolor: outDeviceData?.mqtt_connected
                        ? Colors.status.success
                        : Colors.status.danger,
                      color: Colors.text.white,
                      fontWeight: 900,
                    }}
                  />
                  <Chip
                    label={outPayload?.bag_full_alert ? "Bag Full Alert" : "Bag Normal"}
                    sx={{
                      bgcolor: outPayload?.bag_full_alert
                        ? Colors.status.danger
                        : Colors.status.success,
                      color: Colors.text.white,
                      fontWeight: 900,
                    }}
                  />
                </Stack>

                <Box
                  sx={{
                    px: 1.2,
                    py: 1,
                    borderRadius: 3,
                    background: outStatusTheme.bg,
                    border: `1px solid ${outStatusTheme.border}`,
                    width: "fit-content",
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 900, color: outStatusTheme.color }}>
                    OUT Device Status: {outStatusText}
                  </Typography>
                </Box>
              </Stack>
            </Panel>
          </Grid>

          {/* OUT SESSION */}
          <Grid item xs={12} md={6}>
            <Panel
              title="FLUDEE OUT Live Session"
              right={<SouthRoundedIcon sx={{ color: Colors.text.icon }} />}
              sx={{ background: alpha("#D9B63D", 0.08) }}
            >
              <Stack spacing={1.5}>
                <Grid container spacing={1.2}>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Session Active" value={outPayload?.session_active ? "YES" : "NO"} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Session Paused" value={outPayload?.session_paused ? "YES" : "NO"} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Patient ID" value={fmt(outPayload?.patient_id)} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Current Volume" value={fmt(outCurrentVolumeMl, " ml")} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Output Speed" value={fmt(outSpeedMlHr, " ml/hr")} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Bag Capacity" value={fmt(outBagCapacityMl, " ml")} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Bag Weight" value={fmt(outPayload?.bag_weight_gm, " gm")} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Raw Weight" value={fmt(outPayload?.raw_weight_g, " g")} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Net Weight" value={fmt(outPayload?.net_weight_g, " g")} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Density" value={fmt(outPayload?.fluid_density_g_per_ml, " g/ml")} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Date" value={fmt(outPayload?.date)} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <InfoLabel label="Time" value={fmt(outPayload?.time)} />
                  </Grid>
                </Grid>

                <Box>
                  <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700, mb: 0.8 }}>
                    FLUDEE OUT Bag Fill Level
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={outFillPercent}
                    sx={{
                      height: 14,
                      borderRadius: 999,
                      bgcolor: alpha("#D9B63D", 0.18),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        background: `linear-gradient(90deg, #D9B63D, #f59e0b)`,
                      },
                    }}
                  />
                  <Typography sx={{ mt: 0.8, fontSize: 12, color: Colors.text.body, fontWeight: 800 }}>
                    {Math.round(outFillPercent)}%
                  </Typography>
                </Box>
              </Stack>
            </Panel>
          </Grid>

          {/* DEVICE & THERAPY */}
          <Grid item xs={12}>
            <Panel title="Device & Therapy Information" right={<MedicationOutlinedIcon sx={{ color: Colors.text.icon }} />}>
              <Grid container spacing={1.5}>
                <Grid item xs={6} md={2}>
                  <InfoLabel label="IN Fluid Type" value={fmt(payload?.fluid_type)} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <InfoLabel
                    label="IN Display Rate"
                    value={`${fmt(payload?.display_rate_value)} ${payload?.display_rate_unit || ""}`}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <InfoLabel label="IN Drip Factor" value={fmt(payload?.drip_factor)} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <InfoLabel label="IN Infusion Count" value={fmt(payload?.infusion_count)} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <InfoLabel label="IN RTC Date" value={fmt(payload?.rtc_date)} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <InfoLabel label="OUT Fluid Type" value={fmt(outPayload?.fluid_type)} />
                </Grid>
              </Grid>
            </Panel>
          </Grid>

          {/* BOTTOM SUMMARY */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 4,
                background: Colors.bg.paper,
                border: `1px solid ${Colors.border.light}`,
                boxShadow: Colors.shadow.brand,
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Flow In" value={fmt(flowIn)} big />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Flow Out" value={fmt(flowOut)} big />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="Infusion %" value={`${Math.round(infusionPercent)}%`} big />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoLabel label="IN Battery %" value={`${Math.round(batteryPercent)}%`} big />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 700, mb: 0.8 }}>
                    IN Session Fill Level
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={sessionProgress}
                    sx={{
                      height: 14,
                      borderRadius: 999,
                      bgcolor: alpha(Colors.status.success, 0.12),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${Colors.status.success}, ${Colors.brand.secondary})`,
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* DEBUG */}
          {/* <Grid item xs={12}>
            <Panel title="Raw MQTT Payload">
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: 3,
                  bgcolor: Colors.bg.dark,
                  color: "#dbeafe",
                  overflowX: "auto",
                  fontSize: 12.5,
                  lineHeight: 1.6,
                  border: `1px solid ${alpha("#ffffff", 0.08)}`,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {lastRawPayload || "Waiting for MQTT data..."}
              </Box>
            </Panel>
          </Grid> */}
        </Grid>
      </Stack>
    </Box>
  );
}